/**
 * Resolução de períodos relativos → range absoluto { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }.
 *
 * Convenções:
 *   - Janela end-inclusive em granularidade de dia (00:00 → 23:59).
 *   - "Últimos N dias" inclui o dia de hoje (N=7 → últimos 7 dias incluindo hoje).
 *   - "Mês passado / últimos N meses" usa meses FECHADOS (não inclui o mês corrente).
 *   - "Custom" lê {dateFrom, dateTo} dos overrides.
 */

// TUDO em UTC. Sem getFullYear/Month/Date locais. O sistema só conhece
// `YYYY-MM-DD` UTC — coerente com bucketing do backend e armazenamento UTC.

function pad2(n) {
    return String(n).padStart(2, '0');
}

function toIsoDate(d) {
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

function startOfDay(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0));
}

function endOfDay(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999));
}

function startOfMonth(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0, 0));
}

function endOfMonth(d) {
    // dia 0 do próximo mês = último dia do mês corrente
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0, 23, 59, 59, 999));
}

function startOfQuarter(d) {
    const q = Math.floor(d.getUTCMonth() / 3);
    return new Date(Date.UTC(d.getUTCFullYear(), q * 3, 1, 0, 0, 0, 0));
}

function endOfQuarter(d) {
    const q = Math.floor(d.getUTCMonth() / 3);
    return new Date(Date.UTC(d.getUTCFullYear(), q * 3 + 3, 0, 23, 59, 59, 999));
}

function startOfYear(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), 0, 1, 0, 0, 0, 0));
}

function endOfYear(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), 11, 31, 23, 59, 59, 999));
}

function addDays(d, n) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n, 0, 0, 0, 0));
}

function addMonths(d, n) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1, 0, 0, 0, 0));
}

/**
 * @param {string|null} periodId
 * @param {object} opts { lastNDays?, dateFrom?, dateTo?, now? }
 * @returns {{ from: string, to: string } | null}
 */
export function ResolvePeriodRange(periodId, opts = {}) {
    const now = opts.now instanceof Date ? opts.now : new Date();

    switch (periodId) {
        case 'today': {
            return { from: toIsoDate(startOfDay(now)), to: toIsoDate(endOfDay(now)) };
        }
        case 'yesterday': {
            const y = addDays(now, -1);
            return { from: toIsoDate(startOfDay(y)), to: toIsoDate(endOfDay(y)) };
        }
        case '7d':
        case 'last_7d': {
            const start = addDays(startOfDay(now), -6); // inclui hoje
            return { from: toIsoDate(start), to: toIsoDate(endOfDay(now)) };
        }
        case '15d':
        case 'last_15d': {
            const start = addDays(startOfDay(now), -14);
            return { from: toIsoDate(start), to: toIsoDate(endOfDay(now)) };
        }
        case '28d':
        case 'last_28d': {
            // Janela canônica do preset "Visão Semanal" — bate exatamente com as
            // 4 semanas comparativas (w1..w4 = 7d cada).
            const start = addDays(startOfDay(now), -27);
            return { from: toIsoDate(start), to: toIsoDate(endOfDay(now)) };
        }
        case '30d':
        case 'last_30d': {
            const start = addDays(startOfDay(now), -29);
            return { from: toIsoDate(start), to: toIsoDate(endOfDay(now)) };
        }
        case '60d':
        case 'last_60d': {
            const start = addDays(startOfDay(now), -59);
            return { from: toIsoDate(start), to: toIsoDate(endOfDay(now)) };
        }
        case '90d':
        case 'last_90d': {
            const start = addDays(startOfDay(now), -89);
            return { from: toIsoDate(start), to: toIsoDate(endOfDay(now)) };
        }
        case 'mtd':
        case 'month': {
            // Mês corrente, do dia 1 até hoje.
            return { from: toIsoDate(startOfMonth(now)), to: toIsoDate(endOfDay(now)) };
        }
        case 'last_month': {
            // Mês anterior FECHADO (dia 1 → último dia).
            const prev = addMonths(now, -1);
            return { from: toIsoDate(startOfMonth(prev)), to: toIsoDate(endOfMonth(prev)) };
        }
        case 'last_3_months': {
            // 3 meses fechados anteriores (não inclui o mês corrente).
            const start = startOfMonth(addMonths(now, -3));
            const end = endOfMonth(addMonths(now, -1));
            return { from: toIsoDate(start), to: toIsoDate(end) };
        }
        case 'last_6_months': {
            const start = startOfMonth(addMonths(now, -6));
            const end = endOfMonth(addMonths(now, -1));
            return { from: toIsoDate(start), to: toIsoDate(end) };
        }
        case 'last_12_months': {
            const start = startOfMonth(addMonths(now, -12));
            const end = endOfMonth(addMonths(now, -1));
            return { from: toIsoDate(start), to: toIsoDate(end) };
        }
        case 'qtd':
        case 'quarter': {
            return { from: toIsoDate(startOfQuarter(now)), to: toIsoDate(endOfDay(now)) };
        }
        case 'last_quarter': {
            const prevQuarter = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 - 3, 1);
            return { from: toIsoDate(startOfQuarter(prevQuarter)), to: toIsoDate(endOfQuarter(prevQuarter)) };
        }
        case 'ytd':
        case 'year': {
            return { from: toIsoDate(startOfYear(now)), to: toIsoDate(endOfDay(now)) };
        }
        case 'last_year': {
            const prev = new Date(now.getFullYear() - 1, 0, 1);
            return { from: toIsoDate(startOfYear(prev)), to: toIsoDate(endOfYear(prev)) };
        }
        case 'custom': {
            const from = String(opts.dateFrom || '').trim();
            const to = String(opts.dateTo || '').trim();
            if (!from || !to) {
                return null;
            }
            return { from, to };
        }
        default:
            return null;
    }
}

/** Lista canônica de presets de período exibidos no dropdown. Agrupados por categoria. */
export const PERIOD_GROUPS = [
    {
        id: 'recent',
        label: 'Recentes',
        items: [
            { id: 'today', label: 'Hoje' },
            { id: 'yesterday', label: 'Ontem' },
            { id: 'last_7d', label: 'Últimos 7 dias' },
            { id: 'last_15d', label: 'Últimos 15 dias' },
            { id: 'last_28d', label: 'Últimos 28 dias' },
            { id: 'last_30d', label: 'Últimos 30 dias' },
            { id: 'last_60d', label: 'Últimos 60 dias' },
            { id: 'last_90d', label: 'Últimos 90 dias' },
        ],
    },
    {
        id: 'months',
        label: 'Meses',
        items: [
            { id: 'mtd', label: 'Este mês (até hoje)' },
            { id: 'last_month', label: 'Mês passado' },
            { id: 'last_3_months', label: 'Últimos 3 meses fechados' },
            { id: 'last_6_months', label: 'Últimos 6 meses fechados' },
            { id: 'last_12_months', label: 'Últimos 12 meses fechados' },
        ],
    },
    {
        id: 'quarters_years',
        label: 'Trimestres / Anos',
        items: [
            { id: 'qtd', label: 'Este trimestre' },
            { id: 'last_quarter', label: 'Trimestre passado' },
            { id: 'ytd', label: 'Este ano' },
            { id: 'last_year', label: 'Ano passado' },
        ],
    },
    {
        id: 'custom',
        label: 'Personalizado',
        items: [{ id: 'custom', label: 'Datas específicas…' }],
    },
];

/** Lookup de label por id (para chip ativo). */
export function GetPeriodLabel(periodId) {
    for (const group of PERIOD_GROUPS) {
        const found = group.items.find((it) => it.id === periodId);
        if (found) {
            return found.label;
        }
    }
    return periodId || 'Período';
}

/**
 * Sufixo curto pra exibir junto ao nome do preset. Ex: "Visão Diária · 30d".
 * Mais conciso que `getPeriodLabel` (que devolve a string completa do dropdown).
 */
export function GetPeriodSuffix(periodId) {
    if (!periodId) {
        return null;
    }
    const map = {
        today: 'Hoje',
        yesterday: 'Ontem',
        '7d': '7d',
        last_7d: '7d',
        '15d': '15d',
        last_15d: '15d',
        '28d': '28d',
        last_28d: '28d',
        '30d': '30d',
        last_30d: '30d',
        '60d': '60d',
        last_60d: '60d',
        '90d': '90d',
        last_90d: '90d',
        mtd: 'Mês atual',
        month: 'Mês atual',
        last_month: 'Mês passado',
        last_3_months: '3M fechados',
        last_6_months: '6M fechados',
        last_12_months: '12M fechados',
        qtd: 'Trimestre atual',
        quarter: 'Trimestre atual',
        last_quarter: 'Trimestre passado',
        ytd: 'Ano atual',
        year: 'Ano atual',
        last_year: 'Ano passado',
        custom: 'Personalizado',
    };
    return map[periodId] || periodId;
}

/**
 * Renderiza o range absoluto de um período em formato BR ("19/05/2026 à 21/04/2026").
 * Usado nos tooltips do dropdown — mostra ao usuário exatamente o intervalo aplicado.
 * Retorna null pra `custom` (range vem dos overrides, não do preset) e quando o
 * período não resolve range válido.
 */
export function GetPeriodRangeText(periodId, { now = new Date() } = {}) {
    if (!periodId || periodId === 'custom') {
        return null;
    }
    const range = ResolvePeriodRange(periodId, { now });
    if (!range) {
        return null;
    }
    return `${formatBrDate(range.from)} à ${formatBrDate(range.to)}`;
}

function formatBrDate(iso) {
    // Pure string slicing — ISO YYYY-MM-DD → DD/MM/YYYY. Zero TZ risk.
    const parts = String(iso).split('-');
    if (parts.length !== 3) return String(iso);
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

/**
 * Quantos dias inteiros há entre `from` e `to` (inclusivo).
 * Aceita 'YYYY-MM-DD' ou Date. Tudo interpretado como UTC.
 */
export function DaysBetween(from, to) {
    const a = typeof from === 'string' ? parseIsoUtc(from) : from instanceof Date ? from : null;
    const b = typeof to === 'string' ? parseIsoUtc(to) : to instanceof Date ? to : null;
    if (!a || !b || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
    const aStart = Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate());
    const bStart = Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate());
    const diff = Math.round((bStart - aStart) / (24 * 60 * 60 * 1000));
    return Math.max(0, diff + 1); // inclusivo
}

function parseIsoUtc(str) {
    const parts = String(str)
        .split('-')
        .map((p) => Number.parseInt(p, 10));
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
    return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0));
}
